/**
 * Fill in a class's supplemental questionnaire.
 *
 * Reached from Connor once he has worked out the class, and from the
 * Supplementals library. This is the step that replaces "here's the carrier's
 * PDF, good luck" — the same questions, in the browser, saveable, and splittable
 * between the agent and their insured.
 *
 * The agent/insured toggle is the point of the screen. An agent can answer maybe
 * a dozen of the 144 questions on a restaurant supplemental from their own file;
 * the rest are things only someone standing in the building knows. Rather than
 * making them scroll past a hundred questions they can't answer, the form shows
 * their half — and the other half goes to the insured on a link.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import type { Audience, FormAnswers, SupplementalForm as FormDef } from '@commpass/shared';
import { completeness } from '@commpass/shared';
import { SupplementalForm } from '../components/SupplementalForm';
import { generateSupplementalPdf } from '../lib/supplementalPdf';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../auth/AuthProvider';

/** Answers live here until the response tables exist — see migration 0005. */
const draftKey = (cls: string) => `supp-draft:${cls}`;

/**
 * A ready-to-send email carrying the link and the code.
 *
 * The PIN goes in the SAME message here, which is a deliberate softening of the
 * two-channel rule. The alternative in practice is not a phone call — it is an
 * agent who finds the whole thing awkward and emails a PDF instead. A link plus
 * a code in one email is still better than an unprotected attachment, and the
 * note tells them to send the code separately when the risk warrants it.
 */
function mailtoInsured(
  invite: { link: string; pin: string; expiresAt: string },
  businessName: string,
  to?: string,
): string {
  const who = businessName || 'your business';
  const subject = `Information needed for ${who}'s insurance application`;
  const body = [
    `Hello,`,
    ``,
    `I'm working on the insurance application for ${who} and there are some questions about how the business operates that only you can answer.`,
    ``,
    `You can fill them in here:`,
    invite.link,
    ``,
    `Access code: ${invite.pin}`,
    ``,
    `It saves as you go, so you can stop and come back to the same link. Anything you're unsure about, leave blank and I'll follow up.`,
    ``,
    `The link expires on ${new Date(invite.expiresAt).toLocaleDateString('en-US', { dateStyle: 'long' })}.`,
    ``,
    `Thank you,`,
  ].join('\r\n');
  return `mailto:${encodeURIComponent(to ?? '')}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export function SupplementalFill() {
  const { class: rawClass } = useParams<{ class: string }>();
  const cls = decodeURIComponent(rawClass ?? '');
  const navigate = useNavigate();
  const { session } = useAuth();

  const [form, setForm] = useState<FormDef | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [audience, setAudience] = useState<Audience | undefined>('agent');
  /* Named on the PDF so an underwriter knows whose risk it is; kept out of the
     answers map, which is keyed by the form's own field ids. */
  const [businessName, setBusinessName] = useState('');
  const [answers, setAnswers] = useState<FormAnswers>(() => {
    try {
      return JSON.parse(localStorage.getItem(draftKey(cls)) ?? '{}') as FormAnswers;
    } catch {
      return {};
    }
  });

  useEffect(() => {
    let live = true;
    api
      .getForm(cls)
      .then((f) => live && setForm(f))
      .catch((e: unknown) =>
        live &&
        setError(
          e instanceof ApiError && e.status === 404
            ? `There's no questionnaire for ${cls} yet.`
            : 'Could not load that questionnaire.',
        ),
      );
    return () => {
      live = false;
    };
  }, [cls]);

  /**
   * Two-tier saving, and both tiers earn their place.
   *
   * localStorage is written on every keystroke: instant, offline-proof, and it
   * survives a closed tab. The server copy is what makes the work reachable from
   * another machine, sendable to the insured, and visible to AAS — but it is a
   * network call, so it is debounced and allowed to fail.
   *
   * If the server save fails — the tables aren't there yet, the session lapsed,
   * the wifi dropped — the page says so plainly rather than showing a tick. The
   * work is still in the browser either way, so nothing is lost; what changes is
   * whether the agent should trust it to be anywhere else.
   */
  useEffect(() => {
    localStorage.setItem(draftKey(cls), JSON.stringify(answers));
  }, [cls, answers]);

  const [responseId, setResponseId] = useState<string | null>(() => localStorage.getItem(`supp-id:${cls}`));
  const [saved, setSaved] = useState<'local' | 'saving' | 'synced' | 'offline'>('local');
  const pending = useRef<FormAnswers | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!pending.current || !session) return;
    setSaved('saving');
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const payload = pending.current;
      pending.current = null;
      if (!payload) return;
      void (async () => {
        try {
          let id = responseId;
          // Created on first edit, not on page load — otherwise every idle look
          // at a questionnaire leaves an empty draft behind.
          if (!id) {
            id = (await api.createResponse(cls, businessName.trim() || undefined)).id;
            localStorage.setItem(`supp-id:${cls}`, id);
            setResponseId(id);
          }
          await api.saveResponse(id, { answers: payload, businessName: businessName.trim() || undefined });
          setSaved('synced');
        } catch {
          setSaved('offline');
        }
      })();
    }, 1000);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [answers, cls, responseId, session, businessName]);

  const set = (id: string, value: FormAnswers[string]) =>
    setAnswers((a) => {
      const next = { ...a, [id]: value };
      pending.current = next;
      return next;
    });

  const [invite, setInvite] = useState<{ link: string; pin: string; expiresAt: string } | null>(null);
  const [inviting, setInviting] = useState(false);
  /**
   * Hand the questionnaire to the insured.
   *
   * Deliberately available with nothing filled in. Sending it straight on is a
   * perfectly normal way to work — the insured knows the operational answers and
   * the agent can add their own half later, or never. Requiring an edit first
   * meant an agent who just wanted to forward the form had to type something
   * meaningless to unlock the button.
   */
  const sendToInsured = async () => {
    setInviting(true);
    setError(null);
    try {
      let id = responseId;
      if (!id) {
        id = (await api.createResponse(cls, businessName.trim() || undefined)).id;
        localStorage.setItem(`supp-id:${cls}`, id);
        setResponseId(id);
      }
      // Flush anything still in the debounce first — otherwise the insured opens
      // a form missing the last thing the agent typed.
      if (pending.current) await api.saveResponse(id, { answers: pending.current });
      setInvite(await api.inviteInsured(id));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not create the link.');
    } finally {
      setInviting(false);
    }
  };

  /**
   * Arriving from the library's "Send to my insured" opens the link straight
   * away, so that choice is one click rather than a click plus a hunt for the
   * button at the bottom of a 300-question form.
   */
  const autoSend = new URLSearchParams(useLocation().search).get('send') === '1';
  const autoSent = useRef(false);
  useEffect(() => {
    if (!autoSend || autoSent.current || !form || !session) return;
    autoSent.current = true;
    void sendToInsured();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSend, form, session]);

  const counts = useMemo(() => {
    if (!form) return null;
    return {
      agent: completeness(form, answers, 'agent'),
      insured: completeness(form, answers, 'insured'),
      all: completeness(form, answers),
    };
  }, [form, answers]);

  if (error) {
    return (
      <div className="wrap">
        <div className="empty">
          <p>{error}</p>
          <button className="btn btn-ghost" onClick={() => navigate('/hub/supplementals')}>
            Back to supplementals
          </button>
        </div>
      </div>
    );
  }

  if (!form || !counts) {
    return (
      <div className="wrap">
        <div className="empty">
          <p>Loading the questionnaire…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="wrap">
      <div className="supp-head">
        <div>
          <h2>{form.class}</h2>
          <p className="supp-source">
            Based on {form.source.title}
            {form.source.carrier ? ` (${form.source.carrier})` : ''} · {form.fields.length} questions
          </p>
        </div>
        <a className="btn btn-ghost" href={form.source.url} target="_blank" rel="noopener noreferrer">
          Original PDF
        </a>
      </div>

      {/* Not decoration: this is how the work gets divided. The counts are on the
          tabs so an agent can see at a glance how much is theirs. */}
      <div className="supp-tabs" role="tablist">
        {([
          ['agent', 'Your part', counts.agent],
          ['insured', 'For the insured', counts.insured],
          [undefined, 'Everything', counts.all],
        ] as const).map(([who, label, c]) => (
          <button
            key={label}
            role="tab"
            aria-selected={audience === who}
            className={`supp-tab${audience === who ? ' is-on' : ''}`}
            onClick={() => setAudience(who)}
          >
            {label} <span className="supp-tab-count">{c.answered}/{c.total}</span>
          </button>
        ))}
      </div>

      <SupplementalForm form={form} answers={answers} onChange={set} audience={audience} />

      <div className="supp-actions">
        {/* The PDF is the point of the exercise — it is what an underwriter
            actually reads. It works entirely in the browser, so it does not wait
            on the database the way saving and the insured link do. */}
        <div className="supp-buttons">
          <button
            className="btn btn-primary"
            onClick={() => generateSupplementalPdf(form, answers, { businessName: businessName.trim() || undefined })}
          >
            Download PDF for the underwriter
          </button>
          <input
            className="supp-name"
            placeholder="Insured's business name (for the PDF)"
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            aria-label="Insured's business name"
          />
          {!invite && (
            <button className="btn btn-ghost" disabled={inviting || !session} onClick={() => void sendToInsured()}>
              {inviting ? 'Creating link…' : 'Send to my insured'}
            </button>
          )}
          <button className="btn btn-ghost" onClick={() => setAnswers({})}>
            Clear
          </button>
        </div>
        {invite ? (
          /* Shown once. The PIN is hashed on the server the moment it's made, so
             this is genuinely the only time it can be displayed — which is also
             why it is worth saying so out loud. */
          <div className="supp-invite">
            <strong>Send your insured this link:</strong>
            <code className="supp-link">{invite.link}</code>
            <div className="supp-pin">
              Their code is <strong>{invite.pin}</strong>
            </div>
            {/* Opens their own mail client with everything filled in. It uses
                whatever they already send mail with, so the message comes from
                the agent's real address — which is what makes an insured trust
                a link asking for their payroll. */}
            <div className="supp-buttons" style={{ marginTop: 10 }}>
              <a
                className="btn btn-primary"
                href={mailtoInsured(invite, businessName.trim(), answers['aas_send_form_to_email'] as string)}
              >
                Open this in an email
              </a>
              <button
                className="btn btn-ghost"
                onClick={() => void navigator.clipboard?.writeText(`${invite.link}\n\nAccess code: ${invite.pin}`)}
              >
                Copy link and code
              </button>
            </div>
            <p className="supp-note">
              The email includes the code. For a sensitive account, delete it from the message and
              text it instead — then an intercepted email isn't enough on its own. The code is shown
              here once and can't be retrieved; if it's lost, make a new link. Expires{' '}
              {new Date(invite.expiresAt).toLocaleDateString('en-US', { dateStyle: 'medium' })}.
            </p>
          </div>
        ) : (
          <p className="supp-note">
            {saved === 'synced' && 'Saved to your account — you can pick this up on another computer. '}
            {saved === 'saving' && 'Saving… '}
            {saved === 'offline' && "Saved in this browser only — we couldn't reach the server, so it isn't stored to your account yet. "}
            {saved === 'local' && !session && 'Sign in to save this to your account. '}
            {saved === 'local' && session && 'Answers are saved as you type. '}
            The PDF prints every question — unanswered ones included, so nothing looks like it was
            never asked.
          </p>
        )}
      </div>
    </div>
  );
}
